import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Users, Award, Briefcase, Calendar, Clock, Edit2, Save, X, FileText, CheckCircle2, XCircle, UserCheck, Eye, Trash2, AlertTriangle, Heart } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

import { ReceiptHistory } from '../components/billing/ReceiptHistory';

export default function TeacherProfilePage() {
  const { teacherId: paramId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useApp();
  const teacherId = paramId || currentUser?.userId;
  const isSelf = currentUser?.userId === teacherId;
  // v96 — Individual profiles (no schoolId) get a trimmed profile view:
  // no Assigned Classes, no Students, no Attendance Marked, no Medical
  // Profile. Those cards only make sense for school-teacher accounts.
  const isIndividual = !currentUser?.schoolId;

  const [teacher, setTeacher] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);

  useEffect(() => { loadTeacher(); }, [teacherId]);

  const loadTeacher = async () => {
    setIsLoading(true);
    try {
      const data = await api.getTeacherProfile(teacherId);
      setTeacher(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load teacher profile');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = () => {
    const form = {};
    if (isAdmin) {
      form.name = teacher?.name || '';
      form.email = teacher?.email || '';
      form.phone = teacher?.phone || '';
      form.subject = teacher?.subject || '';
      form.experience = teacher?.experience || '';
      form.qualifications = teacher?.qualifications || '';
      form.assignedClasses = (teacher?.assignedClasses || []).join(', ');
      form.certifications = teacher?.certifications || '';
      form.skills = teacher?.skills || '';
    } else if (isSelf) {
      form.certifications = teacher?.certifications || '';
      form.skills = teacher?.skills || '';
      form.medicalConditions = teacher?.medicalConditions || '';
      form.bloodGroup = teacher?.bloodGroup || '';
    }
    setEditForm(form);
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Validate mandatory fields
    if (isAdmin) {
      if (!editForm.name?.trim()) { toast.error('Name is required'); return; }
      if (!editForm.email?.trim()) { toast.error('Email is required'); return; }
      if (!editForm.phone?.trim()) { toast.error('Phone is required'); return; }
      if (!editForm.qualifications?.trim()) { toast.error('Qualifications is required'); return; }
    }
    setIsSaving(true);
    try {
      const payload = { ...editForm };
      // Parse assignedClasses from comma-separated string to array
      if (payload.assignedClasses !== undefined && typeof payload.assignedClasses === 'string') {
        payload.assignedClasses = payload.assignedClasses.split(',').map(c => c.trim()).filter(Boolean);
      }
      await api.updateTeacher(teacherId, payload);
      toast.success('Profile updated');
      setIsEditing(false);
      loadTeacher();
    } catch (error) {
      toast.error(error.message || 'Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDoc = async () => {
    if (!deletingDoc?.fileId) return;
    try {
      await api.deleteDocument(deletingDoc.fileId);
      toast.success('Document removed');
      setDeletingDoc(null);
      loadTeacher();
    } catch (error) {
      toast.error(error.message || 'Failed to remove document');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="teacher-profile-loading">
        <div className="w-10 h-10 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="text-center py-20" data-testid="teacher-profile-error">
        <p className="text-muted-foreground">Teacher not found.</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  const initials = teacher.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'T';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="teacher-profile-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <PageHeader title="Teacher Profile" description={`${teacher.name}'s profile`} />
      </div>

      {/* Hero Card */}
      <DataCard>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex items-start gap-4 flex-1">
            <Avatar className="w-16 h-16">
              <AvatarFallback className="bg-emerald-600 text-white text-xl font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{teacher.name}</h2>
              <p className="text-sm text-muted-foreground">{teacher.email}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {teacher.subject && <Badge className="bg-blue-500/10 text-blue-600"><BookOpen className="w-3 h-3 mr-1" />{teacher.subject}</Badge>}
                {teacher.experience && <Badge className="bg-amber-500/10 text-amber-600"><Briefcase className="w-3 h-3 mr-1" />{teacher.experience}</Badge>}
                <Badge className="bg-emerald-500/10 text-emerald-600"><Users className="w-3 h-3 mr-1" />{teacher.studentCount || 0} Students</Badge>
              </div>
            </div>
          </div>
          {(isAdmin || isSelf) && !isEditing && (
            <Button variant="outline" onClick={startEditing} data-testid="edit-teacher-btn">
              <Edit2 className="w-4 h-4 mr-2" /> {isSelf && !isAdmin && !isIndividual ? 'Update Medical Info' : 'Edit'}
            </Button>
          )}
        </div>
      </DataCard>

      {/* Stats Row — hidden for Individual profiles (v96) */}
      {!isIndividual && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-brand-deep-blue">{teacher.assignedClasses?.length || 0}</div>
          <p className="text-xs text-muted-foreground mt-1">Assigned Classes</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-emerald-600">{teacher.studentCount || 0}</div>
          <p className="text-xs text-muted-foreground mt-1">Students</p>
        </DataCard>
        <DataCard className="text-center">
          <div className="text-2xl font-bold text-amber-500">{teacher.attendanceSummary?.markedDays || 0}</div>
          <p className="text-xs text-muted-foreground mt-1">Attendance Marked (30d)</p>
        </DataCard>
        {isAdmin ? (
          <DataCard className="text-center">
            <div className="text-2xl font-bold text-purple-600">{teacher.documents?.length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Documents</p>
          </DataCard>
        ) : (
          <DataCard className="text-center">
            <div className="text-2xl font-bold text-purple-600">{teacher.medicalConditions ? 'Yes' : 'None'}</div>
            <p className="text-xs text-muted-foreground mt-1">Medical Profile</p>
          </DataCard>
        )}
      </div>
      )}

      {/* Edit Form */}
      {isEditing ? (
        <DataCard>
          <h3 className="font-semibold mb-4">Edit Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isAdmin && editForm.name !== undefined && (
              <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-teacher-name" /></div>
            )}
            {isAdmin && editForm.email !== undefined && (
              <div><Label>Email</Label><Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="edit-teacher-email" /></div>
            )}
            {isAdmin && editForm.phone !== undefined && (
              <div><Label>Phone</Label><Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} data-testid="edit-teacher-phone" /></div>
            )}
            {isAdmin && editForm.subject !== undefined && (
              <div><Label>Subject</Label><Input value={editForm.subject} onChange={e => setEditForm({...editForm, subject: e.target.value})} data-testid="edit-teacher-subject" /></div>
            )}
            {isAdmin && editForm.experience !== undefined && (
              <div><Label>Experience</Label><Input value={editForm.experience} onChange={e => setEditForm({...editForm, experience: e.target.value})} data-testid="edit-teacher-experience" /></div>
            )}
            {isAdmin && editForm.assignedClasses !== undefined && (
              <div><Label>Assigned Classes (comma-separated, e.g. 9-A, 10-B)</Label><Input value={editForm.assignedClasses} onChange={e => setEditForm({...editForm, assignedClasses: e.target.value})} data-testid="edit-teacher-classes" /></div>
            )}
            {isAdmin && editForm.qualifications !== undefined && (
              <div className="md:col-span-2"><Label>Qualifications</Label><Textarea value={editForm.qualifications} onChange={e => setEditForm({...editForm, qualifications: e.target.value})} rows={2} data-testid="edit-teacher-qualifications" /></div>
            )}
            {editForm.certifications !== undefined && (
              <div className="md:col-span-2"><Label>Certifications</Label><Textarea value={editForm.certifications} onChange={e => setEditForm({...editForm, certifications: e.target.value})} rows={2} placeholder="List your certifications..." data-testid="edit-teacher-certifications" /></div>
            )}
            {editForm.skills !== undefined && (
              <div className="md:col-span-2"><Label>Skills</Label><Textarea value={editForm.skills} onChange={e => setEditForm({...editForm, skills: e.target.value})} rows={2} placeholder="List your skills..." data-testid="edit-teacher-skills" /></div>
            )}
            {editForm.bloodGroup !== undefined && !isIndividual && (
              <>
                <div className="md:col-span-2"><p className="text-xs font-medium text-muted-foreground border-b pb-1 mt-2">Medical Profile</p></div>
                <div><Label>Blood Group</Label>
                  <Select value={editForm.bloodGroup || 'none'} onValueChange={v => setEditForm({...editForm, bloodGroup: v === 'none' ? '' : v})}>
                    <SelectTrigger data-testid="edit-teacher-blood-group"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Medical Conditions</Label><Input value={editForm.medicalConditions || ''} onChange={e => setEditForm({...editForm, medicalConditions: e.target.value})} placeholder="Any medical conditions..." data-testid="edit-teacher-medical" /></div>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsEditing(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-teacher-btn">
              <Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DataCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Details */}
          <DataCard>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-purple-500" /> Qualifications & Experience</h3>
            <div className="space-y-3 text-sm">
              <div><p className="text-muted-foreground">Qualifications</p><p className="font-medium">{teacher.qualifications || 'Not specified'}</p></div>
              <div><p className="text-muted-foreground">Experience</p><p className="font-medium">{teacher.experience || 'Not specified'}</p></div>
              <div><p className="text-muted-foreground">Certifications</p><p className="font-medium">{teacher.certifications || 'Not specified'}</p></div>
              <div><p className="text-muted-foreground">Skills</p><p className="font-medium">{teacher.skills || 'Not specified'}</p></div>
              <div><p className="text-muted-foreground">Phone</p><p className="font-medium">{teacher.phone || '-'}</p></div>
            </div>
          </DataCard>

          {/* Classes */}
          <DataCard>
            <h3 className="font-semibold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-500" /> Assigned Classes</h3>
            {teacher.assignedClasses?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {teacher.assignedClasses.map(c => (
                  <Badge key={c} variant="outline" className="text-sm">{c}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No classes assigned (Subject Teacher)</p>
            )}
          </DataCard>
        </div>
      )}

      {/* Medical Profile — visible for self-view, HIDDEN for individuals (v96 bug-fix) */}
      {isSelf && !isIndividual && (
        <DataCard>
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" /> Medical Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Blood Group</p>
              <p className="text-sm font-medium">{teacher.bloodGroup || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Emergency Contact</p>
              <p className="text-sm font-medium">{teacher.emergencyContact || 'Not specified'}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-muted-foreground">Medical Conditions</p>
              <p className="text-sm font-medium">{teacher.medicalConditions || 'None reported'}</p>
            </div>
          </div>
        </DataCard>
      )}

      {/* Documents — Admin view only */}
      {isAdmin && teacher.documents?.length > 0 && (
        <DataCard>
          <h3 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> Documents</h3>
          <div className="space-y-2">
            {teacher.documents.map((doc, i) => {
              const fileUrl = (doc.storagePath || doc.storage_path) ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.storagePath || doc.storage_path}` : doc.fileUrl;
              return (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`teacher-doc-${i}`}>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{doc.fileName || doc.original_filename || 'Document'}</p>
                      <p className="text-xs text-muted-foreground">{doc.docType || doc.doc_type || 'General'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {fileUrl && (
                      <Button variant="ghost" size="sm" onClick={() => setViewingDoc({ ...doc, fileUrl, fileName: doc.fileName || doc.original_filename || 'Document' })} data-testid={`view-teacher-doc-${i}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setDeletingDoc({ ...doc, fileName: doc.fileName || doc.original_filename || 'Document' })} data-testid={`remove-teacher-doc-${i}`}>
                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DataCard>
      )}

      {/* Leave History */}
      {teacher.leaves?.length > 0 && (
        <DataCard data-testid="teacher-leave-history">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" /> Leave History</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {teacher.leaves.map((leave, i) => (
              <div key={leave.leaveId || i} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`leave-entry-${i}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{leave.leaveType}</span>
                    <Badge className={leave.status === 'approved' ? 'bg-emerald-500/10 text-emerald-600' : leave.status === 'rejected' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}>{leave.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{leave.startDate} to {leave.endDate}{leave.reason ? ` — ${leave.reason}` : ''}</p>
                  {leave.reviewedByName && (
                    <p className="text-xs mt-0.5 flex items-center gap-1" data-testid={`leave-approver-${i}`}>
                      <UserCheck className="w-3 h-3 text-emerald-500" />
                      <span className="text-muted-foreground">{leave.status === 'approved' ? 'Approved' : 'Reviewed'} by</span>
                      <span className="font-medium text-foreground">{leave.reviewedByName}</span>
                      {leave.reviewedAt && <span className="text-muted-foreground">on {new Date(leave.reviewedAt).toLocaleDateString()}</span>}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DataCard>
      )}

      {/* Activity Log */}
      {teacher.activityLog?.length > 0 && (
        <DataCard>
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" /> Recent Activity</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {teacher.activityLog.slice(0, 10).map((log, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                <div className="w-2 h-2 rounded-full bg-brand-light-blue mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-sm">{log.action?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-muted-foreground">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </DataCard>
      )}

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {viewingDoc?.fileName}</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh]">
            {viewingDoc?.fileUrl && (
              viewingDoc.fileName?.toLowerCase().endsWith('.pdf') ? (
                <iframe src={viewingDoc.fileUrl} className="w-full h-full border-0 rounded" title={viewingDoc.fileName} />
              ) : (
                <img src={viewingDoc.fileUrl} alt={viewingDoc.fileName} className="max-w-full max-h-full object-contain mx-auto" />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isIndividual && isSelf && <ReceiptHistory />}

      {/* Delete Document Warning Dialog */}
      <Dialog open={!!deletingDoc} onOpenChange={() => setDeletingDoc(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Remove Document</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove <strong>{deletingDoc?.fileName}</strong>? This action cannot be undone.
          </p>
          <DialogFooter className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setDeletingDoc(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteDoc}>Remove Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
